import { useAuth } from '@/app/AuthContext.jsx';
import {
  useActiveLaneTasks,
  useDocumentTitle,
  useExpFeatures,
  useLaneCounts,
  useTasksHistorial,
} from '@/hooks';
import {
  STAGE_DEFS,
  STAGE_TONE,
  diligenciaDisplay,
  formatCLP,
  formatDeadline,
  taskDiligenciaStage as taskStage,
  titleCase,
} from '@/lib';
import { extractList, extractTotal } from '@/lib/apiList';
import { formatHistoryDate } from '@/lib/diligenciaDisplay';
import DeadlineCell from './DeadlineCell';
import { cancelTask, errorMessage } from '@/lib/recepthor-api';
import {
  EmptyRow,
  FilterButton,
  FilterChips,
  PageHead,
  Pager,
  SearchField,
  SkeletonRows,
  SortHead,
  StagePill,
  Th,
  useConfirm,
  useToast,
} from '@/ui';
import { ArrowRight, Cube, Plus, RefreshIcon, Wallet } from '@/ui/icons';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom';
import './Diligencias.css';
import { actuationLabel } from '@/lib/taskActuation.js';

// Diligencias — the board + table work surface over the real /tasks data.
//   · Activas / Asignadas → the active set (fetched unfiltered), grouped
//     client-side by stage (lib/diligenciaStage); rendered as the pipeline BOARD
//     (4 stage columns + closed rail) or the TABLE (Columnas / Tabla toggle).
//     Both carry checkbox bulk-select + the selbar.
//   · Historial → the server "historial" lane, paged, rendered as the table.
// Stage tone colors the pill/card; the derived Atrasada paints the pill warn;
// expiry shows in the subtext + the urgent row accent (never a red pill).
// Row/card click → the detail (/diligencias/:id).
//
// API gaps (flagged, graceful fallback):
//   · The "Asignadas" tab still mirrors the active set — /tasks has no
//     server-side assignee filter yet. The "Encargado" filter + column read
//     `assigneeName` (the causa's assignee, derived onto the task), client-side.
//   · No actuación-type / comuna on a /tasks list row → the card meta + table
//     secondary fall back to the causa rol. `actuacionOf` / `comunaOf` probe the
//     likely field names so they light up the moment the API adds them.

// How many closed diligencias the board's rail shows before deferring to the
// full history. Four is what the column's height holds without scrolling.
const CLOSED_RAIL_SIZE = 4;

// 4 active stage columns; the 5th "cerrada" rail is rendered after the loop.
const BOARD_COLS = ['borrador', 'cotizando', 'porpagar', 'encurso'];
const STAGE_LABEL = Object.fromEntries(STAGE_DEFS.map((s) => [s.key, s.label]));
const STAGE_STEP = Object.fromEntries(STAGE_DEFS.map((s) => [s.key, s.step]));

function caratulaOf(t) {
  return t.caseTitle ? titleCase(t.caseTitle) : t.caseRol || '—';
}
function dilId(t) {
  return t.displayId || t.code || t.caseRol || '—';
}
// What kind of judicial act + where — falls back gracefully until /tasks carries
// these (API gap). Used for the card meta + the table secondary line.
function actuacionOf(t) {
  return actuationLabel(t) || t.notificationType || t.actuacion || null;
}
function comunaOf(t) {
  return t.commune || t.comuna || null;
}
// Who executed the diligencia — surfaced first on the closed card meta
// (`receptor · comuna`). Falls back to the actuación type until /tasks carries
// the receptor name on a closed row (API gap).
function receptorOf(t) {
  // Receptor ≠ encargado: the receptor executes the actuación; the encargado
  // (assigneeName) is the firm's internal owner. Do NOT fall back to one for the
  // other.
  return t.receptorName || t.receptor || null;
}
// Closed-card meta: receptor first (who did it), then comuna; the actuación
// type stands in for the receptor while the API doesn't expose it.
function closedMetaOf(t) {
  const lead = receptorOf(t) || actuacionOf(t);
  const parts = [lead, comunaOf(t)].filter(Boolean);
  return parts.length ? parts.join(' · ') : t.caseRol || '—';
}
function metaOf(t) {
  const parts = [actuacionOf(t), comunaOf(t)].filter(Boolean);
  return parts.length ? parts.join(' · ') : t.caseRol || '—';
}
function tableSecondaryOf(t) {
  // Skip the rol when it already is the shown ID (no displayId yet → API gap),
  // so the line doesn't read "C-123 · C-123".
  const rol = dilId(t) === t.caseRol ? null : t.caseRol;
  const parts = [rol, actuacionOf(t), comunaOf(t)].filter(Boolean);
  return parts.join(' · ') || t.caseRol || '—';
}

// Sticky UI state: the chosen tab + board/table view survive a reload via
// sessionStorage.
function useStickyState(key, initial) {
  const [v, setV] = useState(() => {
    try {
      return sessionStorage.getItem(key) ?? initial;
    } catch {
      return initial;
    }
  });
  const set = (next) => {
    setV(next);
    try {
      sessionStorage.setItem(key, next);
    } catch {
      /* ignore */
    }
  };
  return [v, set];
}

// Action affordance per stage: "Pagar" (accent) when the diligencia is in the
// pay window; otherwise a quiet em dash (no busy-work action over real data).
// onPay receives the task so the caller can route to ITS detail (REC-1117).
function ActionBtn({ t, onPay }) {
  const st = taskStage(t);
  if (st.stage === 'porpagar') {
    return (
      <button
        className="t-btn t-btn--sm t-btn--primary"
        onClick={(e) => {
          e.stopPropagation();
          onPay(t);
        }}
      >
        Pagar
      </button>
    );
  }
  return <span className="note">—</span>;
}

function DiligenciaCard({ t, onOpen, sel, setSel, onPay }) {
  const st = taskStage(t);
  // Bulk select is Próx → shown only when experimental features are on.
  const [exp] = useExpFeatures();
  return (
    <div
      className={`dcard dcard--${st.tone}`}
      onClick={() => onOpen(t.id)}
      role="button"
      tabIndex={0}
    >
      <div className="dcard__top">
        {/* Próx: selección múltiple (pago en lote). */}
        {exp && (
          <label className="t-check" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={!!sel[t.id]}
              onChange={() => setSel((s) => ({ ...s, [t.id]: !s[t.id] }))}
            />
          </label>
        )}
        <span className="tbl__id">{dilId(t)}</span>
        <span className="dcard__vence">
          <DeadlineCell t={t} compact />
        </span>
      </div>
      <div className="dcard__partes">{caratulaOf(t)}</div>
      <div className="dcard__meta">{metaOf(t)}</div>
      <div className="dcard__foot">
        <span className="dcard__status">{st.statusText}</span>
        <ActionBtn t={t} onPay={onPay} />
      </div>
    </div>
  );
}

// A closed diligencia card (the board's "Cerrada" rail). The foot carries the
// closure status pill plus a quiet "+ Nueva" CTA to re-encargar in the same
// causa. An expired card swaps it for "Volver a cotizar" (REC-1086): its causa
// is still occupied by the expired diligencia, so the flow closes it first.
function DiligenciaCardClosed({ t, onOpen, onNueva, onRecotizar }) {
  const d = diligenciaDisplay(t.stage, t.deadline, Date.now(), {
    deadlineExpiresAt: t.deadlineExpiresAt,
  });
  const tone = d.dot === 'ok' ? 'ok' : d.row === 'err' ? 'err' : 'mute';
  const upd = formatHistoryDate(t.closedAt || t.updatedAt);
  return (
    <div
      className={`dcard dcard--closed dcard--${tone}`}
      onClick={() => onOpen(t.id)}
      role="button"
      tabIndex={0}
    >
      <div className="dcard__top">
        <span className="tbl__id">{dilId(t)}</span>
        <span className="dcard__vence note">{upd.numeric}</span>
      </div>
      <div className="dcard__partes">{caratulaOf(t)}</div>
      <div className="dcard__meta">{closedMetaOf(t)}</div>
      <div className="dcard__foot">
        <span className={`dcard__status st st--${tone}`}>
          <span className="st__dot" />
          {d.label}
        </span>
        {d.expired ? (
          <button
            type="button"
            className="t-btn t-btn--quiet t-btn--sm"
            title="Volver a cotizar en esta causa"
            onClick={(e) => {
              e.stopPropagation();
              onRecotizar(t);
            }}
          >
            <RefreshIcon size={13} />
            Volver a cotizar
          </button>
        ) : (
          <button
            type="button"
            className="t-btn t-btn--quiet t-btn--sm"
            title="Nueva diligencia en esta causa"
            onClick={(e) => {
              e.stopPropagation();
              onNueva(t);
            }}
          >
            <Plus size={13} />
            Nueva
          </button>
        )}
      </div>
    </div>
  );
}

// How many cards a board column shows before "Ver más"; each click reveals one
// more page of this size — and only for the column clicked (never all columns,
// or you lose the rest of the board's context).
const BOARD_COL_PAGE = 5;

function Board({
  active,
  closed,
  closedTotal,
  onOpen,
  onNueva,
  onRecotizar,
  sel,
  setSel,
  onPay,
  goHist,
}) {
  // Per-column reveal count (keyed by stage). Defaults to BOARD_COL_PAGE.
  const [shown, setShown] = useState({});
  const byStage = {};
  BOARD_COLS.forEach((c) => {
    byStage[c] = [];
  });
  active.forEach((t) => {
    const k = taskStage(t).stage;
    if (byStage[k]) byStage[k].push(t);
  });
  return (
    <div className="board-shell">
      <div className="board">
        {BOARD_COLS.map((c) => {
          const all = byStage[c];
          const limit = shown[c] || BOARD_COL_PAGE;
          const vis = all.slice(0, limit);
          const rest = all.length - vis.length;
          return (
            <div key={c} className="board__col">
              <div className="board__head">
                <span className={`stagepill stagepill--${STAGE_TONE[c]}`}>
                  <span className="st__dot" />
                  {STAGE_LABEL[c]}
                </span>
                <span className="board__n">{all.length}</span>
              </div>
              <div className="board__cards">
                {all.length ? (
                  vis.map((t) => (
                    <DiligenciaCard
                      key={t.id}
                      t={t}
                      onOpen={onOpen}
                      sel={sel}
                      setSel={setSel}
                      onPay={onPay}
                    />
                  ))
                ) : (
                  <div className="board__empty">Sin diligencias</div>
                )}
              </div>
              {rest > 0 && (
                <button
                  className="board__more"
                  onClick={() =>
                    setShown((s) => ({ ...s, [c]: limit + BOARD_COL_PAGE }))
                  }
                >
                  Ver {Math.min(rest, BOARD_COL_PAGE)} más{' '}
                  <ArrowRight size={12} />
                </button>
              )}
            </div>
          );
        })}
        <div className="board__col board__col--closed">
          <div className="board__head">
            {/* Closed-rail head is hardcoded mute (gray) — it is the rail
                label, not a live STAGE_TONE.cerrada pill. */}
            <span className="stagepill stagepill--mute">
              <span className="st__dot" />
              {STAGE_LABEL.cerrada}
            </span>
            <span className="board__n">{closedTotal}</span>
          </div>
          <div className="board__cards">
            {closed.length ? (
              closed.map((t) => (
                <DiligenciaCardClosed
                  key={t.id}
                  t={t}
                  onOpen={onOpen}
                  onNueva={onNueva}
                  onRecotizar={onRecotizar}
                />
              ))
            ) : (
              <div className="board__empty">Aún sin diligencias cerradas</div>
            )}
          </div>
          {closedTotal > 0 && (
            <button className="board__more" onClick={goHist}>
              Ver historial completo <ArrowRight size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── sort over the active table ───────────────────────────────────────────────
const deadKey = (t) => {
  const d = formatDeadline(t.deadline, Date.now(), t.deadlineExpiresAt);
  if (d.numeric === '—') return Number.POSITIVE_INFINITY;
  const m = /(-?\d+)/.exec(d.delta || '');
  if (!m) return 0;
  return /^hace /.test(d.delta) ? -Number(m[1]) : Number(m[1]);
};
function sortDils(arr, sort) {
  if (!sort.key) return arr;
  const dir = sort.dir === 'asc' ? 1 : -1;
  const val = (t) =>
    sort.key === 'dil'
      ? caratulaOf(t)
      : sort.key === 'estado'
        ? (STAGE_STEP[taskStage(t).stage] ?? 9)
        : sort.key === 'deadline'
          ? deadKey(t)
          : sort.key === 'cerrada'
            ? new Date(t.closedAt || t.updatedAt || 0).getTime()
            : '';
  return [...arr].sort((a, b) => {
    const x = val(a);
    const y = val(b);
    return (x < y ? -1 : x > y ? 1 : 0) * dir;
  });
}

export default function Diligencias() {
  useDocumentTitle('Diligencias');
  const navigate = useNavigate();
  const location = useLocation();
  const { setBreadcrumb, setPills } = useOutletContext();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { organizationId, isInternal } = useAuth();
  // Bulk select ("Pagar N" + selbar) is Próx → gated behind experimental features.
  const [exp] = useExpFeatures();
  // Deep-link filters (dashboard KPIs): ?stage=<key> seeds the Etapa filter,
  // ?attn=1 seeds the "Requiere acción" filter. Read once at mount.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useStickyState('dil-tab', 'activas');
  const [view, setView] = useStickyState('dil-view', 'board');
  const [q, setQ] = useState('');
  const [stageSel, setStageSel] = useState(() => {
    const s = searchParams.get('stage');
    return ['borrador', 'cotizando', 'porpagar', 'encurso'].includes(s)
      ? [s]
      : [];
  }); // Activas/Asignadas · Etapa filter
  const [attnOnly, setAttnOnly] = useState(
    () => searchParams.get('attn') === '1',
  ); // Activas/Asignadas · "Requiere acción" filter
  const [estSel, setEstSel] = useState([]); // Historial · Resultado filter
  const [actSel, setActSel] = useState([]); // Historial · Actuación filter
  const [encSel, setEncSel] = useState([]); // Activas · Encargado filter
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [sel, setSel] = useState({});
  const [page, setPage] = useState(1);
  // Default page size is 8; the FILAS select offers 8/16/24/48. Shared across
  // the active client-side table and the server-paged Historial.
  const [pageSize, setPageSize] = useState(8);
  const isHist = tab === 'historial';
  const isAct = !isHist; // activas + asignadas share the active board/table
  const isBoard = isAct && view === 'board';

  useEffect(() => {
    setBreadcrumb(['Diligencias']);
    setPills([]);
    return () => {
      setBreadcrumb([]);
      setPills([]);
    };
  }, [setBreadcrumb, setPills]);
  // A deep-link filter (?stage / ?attn from a dashboard KPI) targets the active
  // board, so land on the Activas tab regardless of the sticky last-tab. Mount
  // only; the seeded filters live in state from here on.
  useEffect(() => {
    if (searchParams.get('stage') || searchParams.get('attn') === '1') {
      setTab('activas');
    }
  }, []);
  // Reset to page 1 whenever the result set changes (tab + filters + sort +
  // size).
  useEffect(() => {
    setPage(1);
  }, [
    tab,
    q,
    stageSel,
    attnOnly,
    estSel,
    actSel,
    sort.key,
    sort.dir,
    pageSize,
  ]);

  const { data: laneCounts } = useLaneCounts(organizationId);

  // Active tasks (board + table) — unfiltered, grouped by stage client-side.
  // Fetched on every tab (not gated by !isHist) so the Activas/Asignadas tab
  // counts stay populated even while Historial is selected. The query is cached
  // so returning to Activas is instant.
  const { data: activeData, isLoading: activeLoading } = useActiveLaneTasks(
    organizationId,
    { placeholderData: keepPreviousData },
  );
  const allTasks = useMemo(
    () => (activeData ? extractList(activeData) : []),
    [activeData],
  );
  const active = useMemo(
    () => allTasks.filter((t) => taskStage(t).stage !== 'cerrada'),
    [allTasks],
  );
  // The closed rail asks the historial lane for its newest four, and does not
  // sieve them out of the active set. That set is one page of a hundred rows
  // ordered by deadline ascending, so sieving it answered the four closed
  // diligencias with the OLDEST deadlines among whatever that page happened to
  // hold, and answered nothing at all for an organization whose closures fall
  // outside it. The head already counts the lane, so the count and the cards
  // now describe the same collection.
  const { data: closedData } = useTasksHistorial(
    { organizationId, page: 1, pageSize: CLOSED_RAIL_SIZE },
    { enabled: organizationId != null },
  );
  const closedSample = useMemo(
    () => (closedData ? extractList(closedData) : []),
    [closedData],
  );
  // The lane's own total, which the same response carries. It is the fallback
  // for the head when the counts endpoint has not answered: the length of the
  // rail is not, since the rail is capped at four by construction and would
  // report four for an organization with forty closures.
  const closedLaneTotal = closedData
    ? extractTotal(closedData, closedSample)
    : 0;

  // Historial — the server lane, paged.
  const {
    data: histData,
    isError: histFailed,
    isLoading: histLoading,
  } = useTasksHistorial(
    { organizationId, page, pageSize, q },
    { enabled: organizationId != null && isHist },
  );
  const histRows = useMemo(
    () => (histData ? extractList(histData) : []),
    [histData],
  );
  const histTotal = histData ? extractTotal(histData, histRows) : 0;
  const histPageCount = Math.max(1, Math.ceil(histTotal / pageSize));

  const isLoading =
    organizationId == null || (isHist ? histLoading : activeLoading);
  const openDil = (id) => navigate(`/diligencias/${id}`);
  // /pagos is the BATCH flow's home (selbar "Pagar N") only; a single task's
  // "Pagar" CTA opens its detail via openDil, same as the card body (REC-1117).
  // The ready-to-pay selection travels in the navigation state so the cola
  // arrives pre-checked — the user doesn't reselect what they already chose.
  const goPagos = () => navigate('/pagos', { state: { taskIds: selListo } });
  // Re-encargar from a closed card: open the new-diligencia flow on the same
  // causa. The source task id lets the form preload its notification data
  // (REC-1066). `background` keeps this list mounted under the modal (VD-8).
  const goNueva = (t) =>
    navigate('/diligencias/nueva', {
      state: {
        ...(t?.caseId
          ? {
              caseId: t.caseId,
              rol: t.caseRol,
              caratula: t.caseTitle ? titleCase(t.caseTitle) : undefined,
              fromTaskId: t.id,
            }
          : {}),
        background: location,
      },
    });
  // Volver a cotizar from an expired closed card (REC-1086): unlike a
  // finalizada, the causa is still occupied by the expired diligencia — close
  // it first (the cancel transition frees it; the reason keeps the audit trail
  // distinguishable from a manual cancel), then open the prefilled create
  // flow. The backend 409s if a payin is mid-flight; the error toast surfaces
  // it.
  const [recotizando, setRecotizando] = useState(false);
  const recotizarExpirada = async (t) => {
    if (recotizando) return;
    const ok = await confirm({
      title: 'Volver a cotizar',
      body: (
        <p className="confirm-lead">
          Se archiva la diligencia expirada de la causa {t.caseRol || '—'} (la
          causa queda libre) y se abre una nueva con los mismos datos para
          solicitar cotizaciones otra vez. No se cobró nada.
        </p>
      ),
      confirmLabel: 'Volver a cotizar',
      confirmVariant: 'seal',
    });
    if (!ok) return;
    setRecotizando(true);
    const toastId = toast({
      kind: 'loading',
      message: 'Archivando la diligencia expirada…',
      dismissLabel: 'en curso',
    });
    try {
      await cancelTask(t.id, {
        reason: 'volver a cotizar: diligencia expirada',
      });
      await queryClient.invalidateQueries();
      toast.update(toastId, {
        kind: 'success',
        message: 'Diligencia archivada. La causa quedó libre.',
        duration: 4000,
      });
      goNueva(t);
    } catch (err) {
      console.error('cancelTask failed', err);
      toast.update(toastId, {
        kind: 'err',
        message: errorMessage(
          err,
          'No se pudo archivar la diligencia expirada.',
        ),
        duration: 0,
      });
    } finally {
      setRecotizando(false);
    }
  };

  // ── active filtering (Etapa + search) ──
  const ql = q.trim().toLowerCase();
  const activeFiltered = useMemo(
    () =>
      active.filter((t) => {
        const st = taskStage(t);
        if (stageSel.length && !stageSel.includes(st.stage)) {
          return false;
        }
        // "Requiere acción": attn is already false for terminal rows (expired
        // included) at the source, matching the Inicio foco definition.
        if (attnOnly && !st.attn) {
          return false;
        }
        if (
          ql &&
          !`${dilId(t)} ${t.caseRol || ''} ${t.caseTitle || ''} ${actuacionOf(t) || ''} ${comunaOf(t) || ''}`
            .toLowerCase()
            .includes(ql)
        ) {
          return false;
        }
        if (encSel.length && !encSel.includes(t.assigneeName)) {
          return false;
        }
        return true;
      }),
    [active, ql, stageSel, attnOnly, encSel],
  );
  // Board order: needs-action first, then by plazo. Table default mirrors it
  // until the user clicks a sortable header.
  const prioritized = useMemo(() => {
    const attnRank = (t) => (taskStage(t).attn ? 0 : 1);
    return [...activeFiltered].sort((a, b) =>
      attnRank(a) !== attnRank(b)
        ? attnRank(a) - attnRank(b)
        : deadKey(a) - deadKey(b),
    );
  }, [activeFiltered]);
  const activeTableSorted = useMemo(
    () => (sort.key ? sortDils(activeFiltered, sort) : prioritized),
    [activeFiltered, prioritized, sort],
  );
  // The active table is paginated too: even at a handful of rows the "from–to de
  // total · FILAS" footer shows. The page is clamped so a shrinking result set
  // can't strand us past the last page.
  const activePageCount = Math.max(
    1,
    Math.ceil(activeTableSorted.length / pageSize),
  );
  const activePage = Math.min(page, activePageCount);
  const activeTableRows = useMemo(
    () =>
      activeTableSorted.slice(
        (activePage - 1) * pageSize,
        activePage * pageSize,
      ),
    [activeTableSorted, activePage, pageSize],
  );

  // ── Historial filters (Resultado + Actuación) over the loaded page ──
  // The keys belong to this filter, not to any stored enum: they are matched
  // against the derived reading below. 'EXPIRED' in particular labels the
  // reading born of a lapsed deadline, which no column persists.
  const histEstOpts = useMemo(() => {
    const RES = [
      ['CLOSED', 'Finalizada'],
      ['EXPIRED', 'Expirada'],
      ['CANCELLED', 'Cancelada'],
    ];
    const labelOf = (t) =>
      diligenciaDisplay(t.stage, t.deadline, Date.now(), {
        deadlineExpiresAt: t.deadlineExpiresAt,
      }).label;
    return RES.map(([k, label]) => ({
      value: k,
      label,
      n: histRows.filter((t) => labelOf(t) === label).length,
    })).filter((o) => o.n > 0);
  }, [histRows]);
  const histActOpts = useMemo(() => {
    const acts = [...new Set(histRows.map(actuacionOf).filter(Boolean))];
    return acts.map((a) => ({
      value: a,
      label: a,
      n: histRows.filter((t) => actuacionOf(t) === a).length,
    }));
  }, [histRows]);
  // Etapa options for the active tabs: the 4 active stage buckets present in the
  // loaded set, with live counts — derived client-side from taskStage, so this
  // is NOT an API gap.
  const stageOpts = useMemo(
    () =>
      STAGE_DEFS.filter(
        (s) =>
          s.key !== 'cerrada' &&
          active.some((t) => taskStage(t).stage === s.key),
      ).map((s) => ({
        value: s.key,
        label: s.label,
        n: active.filter((t) => taskStage(t).stage === s.key).length,
      })),
    [active],
  );
  // Encargado options for Activas: distinct assignee names on the loaded active
  // set, with live counts. Enabled now that /tasks exposes `assigneeName`.
  const encOpts = useMemo(() => {
    const names = [
      ...new Set(active.map((t) => t.assigneeName).filter(Boolean)),
    ];
    return names.map((name) => ({
      value: name,
      label: name,
      n: active.filter((t) => t.assigneeName === name).length,
    }));
  }, [active]);
  const histFiltered = useMemo(() => {
    const labelOf = (t) =>
      diligenciaDisplay(t.stage, t.deadline, Date.now(), {
        deadlineExpiresAt: t.deadlineExpiresAt,
      }).label;
    const labelByKey = {
      CLOSED: 'Finalizada',
      EXPIRED: 'Expirada',
      CANCELLED: 'Cancelada',
    };
    return histRows.filter((t) => {
      if (estSel.length && !estSel.some((k) => labelByKey[k] === labelOf(t))) {
        return false;
      }
      if (actSel.length && !actSel.includes(actuacionOf(t))) return false;
      return true;
    });
  }, [histRows, estSel, actSel]);

  const selIds = Object.keys(sel).filter((k) => sel[k]);
  // "Pagar N" acts only on the ready-to-pay selection.
  const selListo = useMemo(
    () =>
      selIds.filter((id) => {
        const t = active.find((x) => x.id === id);
        return t && taskStage(t).stage === 'porpagar';
      }),
    [selIds, active],
  );

  // Tab counts always show, regardless of the selected tab. The active query
  // runs on every tab, so `active.length` is the live count of non-closed
  // diligencias on Activas/Asignadas alike.
  const activasCount = active.length || undefined;
  const tabs = [
    { key: 'activas', label: 'Activas', count: activasCount },
    // Asignadas: no assignee on /tasks yet → mirrors the active set (API gap).
    { key: 'asignadas', label: 'Asignadas', count: activasCount },
    {
      key: 'historial',
      label: 'Historial',
      count: laneCounts ? laneCounts.historial : histTotal || undefined,
    },
  ];

  // "Filtros" panel + chips, per tab:
  //   · Activas   → [Prioridad, Etapa, Encargado] — Encargado reads assigneeName
  //                 (the causa's assignee); Etapa buckets the served stage.
  //   · Asignadas → [Prioridad, Etapa, Encargado]
  //   · Historial → [Resultado, Actuación] — Actuación suppressed while empty
  //                 (no actuación type on the list row yet, API gap), so the
  //                 panel never shows a dangling empty group header.
  // Groups whose options are empty are filtered out before reaching the shared
  // FilterButton/FilterChips primitives.
  // Count of diligencias that need action now (attn is false for terminal rows,
  // expired included). Feeds the "Requiere acción" filter chip and mirrors the
  // Inicio foco count.
  const attnCount = useMemo(
    () => active.filter((t) => taskStage(t).attn).length,
    [active],
  );
  const filterGroups = useMemo(() => {
    const groups = isHist
      ? [
          {
            key: 'resultado',
            label: 'Resultado',
            value: estSel,
            options: histEstOpts,
            onChange: setEstSel,
          },
          {
            key: 'actuacion',
            label: 'Actuación',
            value: actSel,
            options: histActOpts,
            onChange: setActSel,
          },
        ]
      : [
          {
            key: 'prioridad',
            label: 'Prioridad',
            value: attnOnly ? ['attn'] : [],
            options: attnCount
              ? [{ value: 'attn', label: 'Requiere acción', n: attnCount }]
              : [],
            onChange: (v) => setAttnOnly(v.includes('attn')),
          },
          {
            key: 'etapa',
            label: 'Etapa',
            value: stageSel,
            options: stageOpts,
            onChange: setStageSel,
          },
          {
            key: 'encargado',
            label: 'Encargado',
            value: encSel,
            options: encOpts,
            onChange: setEncSel,
          },
        ];
    return groups.filter((g) => g.options.length > 0);
  }, [
    isHist,
    estSel,
    actSel,
    stageSel,
    attnOnly,
    attnCount,
    histEstOpts,
    histActOpts,
    stageOpts,
    encSel,
    encOpts,
  ]);

  const switchTab = (t) => {
    setTab(t);
    setSel({});
    setStageSel([]);
    setAttnOnly(false);
    setEstSel([]);
    setActSel([]);
    setEncSel([]);
    setSort({ key: null, dir: 'asc' });
    setQ('');
  };
  const onSort = (key, dir) => setSort({ key, dir });

  const actions = (
    <>
      {/* Próx: se habilita con Funciones experimentales (sin backend aún → demo). */}
      {isInternal && (
        <button
          className="t-btn t-btn--quiet"
          type="button"
          disabled={!exp}
          title="Próximamente"
        >
          <Cube size={15} />
          Crear en lote
          <span className="soon-badge">Próx</span>
        </button>
      )}
      <button
        className="t-btn t-btn--primary"
        type="button"
        onClick={() =>
          navigate('/diligencias/nueva', { state: { background: location } })
        }
      >
        <Plus size={15} />
        Nueva diligencia
        <span className="kbd">D</span>
      </button>
    </>
  );

  const activeEmpty = isBoard
    ? prioritized.length === 0
    : activeTableSorted.length === 0;

  return (
    <div className="page reveal">
      <PageHead
        kicker="Operación"
        title="Diligencias"
        sub="Encarga diligencias judiciales. Espera las ofertas y elige la que más te convenga."
        action={actions}
      />
      <div className="tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`tab${tab === t.key ? ' tab--on' : ''}`}
            onClick={() => switchTab(t.key)}
          >
            {t.label}
            {t.count != null && <span className="tab__n">{t.count}</span>}
          </button>
        ))}
      </div>

      <div className="toolbar">
        <SearchField
          value={q}
          onChange={setQ}
          placeholder="Buscar por ID, carátula o causa…"
        />
        <div className="toolbar__spacer" />
        {isAct && (
          <div className="seg" title="Columnas o tabla">
            <button
              type="button"
              className={view === 'board' ? 'is-on' : ''}
              onClick={() => setView('board')}
            >
              Columnas
            </button>
            <button
              type="button"
              className={view === 'tabla' ? 'is-on' : ''}
              onClick={() => setView('tabla')}
            >
              Tabla
            </button>
          </div>
        )}
        {filterGroups.length > 0 && <FilterButton groups={filterGroups} />}
      </div>

      <FilterChips groups={filterGroups} />

      {/* Próx: barra de selección múltiple (pago en lote). Recorrido actual:
          single — abrir la diligencia y pagarla desde su detalle. */}
      {exp && selIds.length > 0 && isAct && (
        <div className="selbar">
          <span className="selbar__n">
            <b>{selIds.length}</b> seleccionada{selIds.length > 1 ? 's' : ''}
          </span>
          <div className="selbar__sp" />
          {selListo.length > 0 && (
            <button
              className="t-btn t-btn--primary t-btn--sm"
              onClick={goPagos}
            >
              <Wallet size={14} />
              Pagar {selListo.length}
            </button>
          )}
          <button
            className="t-btn t-btn--ghost t-btn--sm"
            type="button"
            onClick={() => setSel({})}
          >
            Limpiar
          </button>
        </div>
      )}

      {isHist ? (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <SortHead
                  label="Diligencia"
                  k="dil"
                  sortKey={sort.key}
                  sortBy={sort.dir}
                  onSort={onSort}
                />
                <SortHead
                  label="Estado"
                  k="estado"
                  sortKey={sort.key}
                  sortBy={sort.dir}
                  onSort={onSort}
                  w="28%"
                />
                <SortHead
                  label="Solicitada para"
                  k="deadline"
                  sortKey={sort.key}
                  sortBy={sort.dir}
                  onSort={onSort}
                  w="16%"
                  num
                  tip="Plazo solicitado por el estudio"
                />
                <SortHead
                  label="Cerrada"
                  k="cerrada"
                  sortKey={sort.key}
                  sortBy={sort.dir}
                  onSort={onSort}
                  w="14%"
                  num
                />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <SkeletonRows rows={8} cols={4} />
              ) : histFiltered.length === 0 ? (
                <EmptyRow
                  span={4}
                  title={
                    ql
                      ? `Sin resultados para "${ql}"`
                      : estSel.length || actSel.length
                        ? 'Ninguna diligencia en este filtro'
                        : 'Historial vacío'
                  }
                  hint="Las diligencias finalizadas y expiradas se archivan aquí."
                />
              ) : (
                (sort.key ? sortDils(histFiltered, sort) : histFiltered).map(
                  (t) => {
                    const d = diligenciaDisplay(
                      t.stage,
                      t.deadline,
                      Date.now(),
                      { deadlineExpiresAt: t.deadlineExpiresAt },
                    );
                    const tone =
                      d.dot === 'ok' ? 'ok' : d.row === 'err' ? 'err' : 'mute';
                    const upd = formatHistoryDate(t.closedAt || t.updatedAt);
                    return (
                      <tr key={t.id} onClick={() => openDil(t.id)}>
                        <td>
                          <div className="tbl__id">{dilId(t)}</div>
                          <div
                            className="tbl__primary"
                            style={{ fontWeight: 400, marginTop: 3 }}
                          >
                            {caratulaOf(t)}
                          </div>
                          <div className="tbl__secondary">
                            {tableSecondaryOf(t)}
                          </div>
                        </td>
                        <td>
                          <div>
                            <span className={`st st--${tone}`}>
                              <span className="st__dot" />
                              {d.label}
                            </span>
                            {t.paidTotal != null || t.paidAmount != null ? (
                              <div
                                className="tbl__secondary"
                                style={{ marginTop: 4 }}
                              >
                                {formatCLP(t.paidTotal ?? t.paidAmount)}
                              </div>
                            ) : null}
                          </div>
                        </td>
                        <td className="num">
                          <DeadlineCell t={t} />
                        </td>
                        <td className="num note">{upd.numeric}</td>
                      </tr>
                    );
                  },
                )
              )}
            </tbody>
          </table>
          <Pager
            page={page}
            pageCount={histPageCount}
            total={histTotal}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={(n) => {
              setPageSize(n);
              setPage(1);
            }}
            sizes={[8, 16, 24, 48]}
            unit="diligencias"
          />
        </div>
      ) : isLoading ? (
        <div className="tbl-wrap" style={{ marginTop: 14 }}>
          <table className="tbl">
            <tbody>
              <SkeletonRows rows={6} cols={4} />
            </tbody>
          </table>
        </div>
      ) : activeEmpty ? (
        <div className="tbl-wrap" style={{ marginTop: 14 }}>
          <table className="tbl">
            <tbody>
              {/* Una lectura caída y un filtro sin resultados rendían la misma
                  fila, y la segunda invita a ajustar un filtro que no es el
                  problema. */}
              <EmptyRow
                span={5}
                title={
                  histFailed && isHist
                    ? 'No se pudo cargar el historial'
                    : ql
                      ? `Sin resultados para "${ql}"`
                      : stageSel.length || attnOnly
                        ? 'Ninguna diligencia en este filtro'
                        : 'No hay diligencias aquí'
                }
                hint={
                  histFailed && isHist
                    ? 'La lectura falló. Reintenta en unos segundos.'
                    : 'Crea una diligencia desde una causa o ajusta el filtro.'
                }
              />
            </tbody>
          </table>
          <Pager
            page={1}
            pageCount={1}
            total={0}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={(n) => {
              setPageSize(n);
              setPage(1);
            }}
            sizes={[8, 16, 24, 48]}
            unit="diligencias"
          />
        </div>
      ) : isBoard ? (
        // Board is a kanban — it shows every card, capped per column with a
        // per-column "Ver más". Pagination belongs to the table view only.
        <Board
          active={prioritized}
          closed={closedSample}
          closedTotal={
            laneCounts
              ? laneCounts.historial || closedLaneTotal
              : closedLaneTotal
          }
          onOpen={openDil}
          onNueva={goNueva}
          onRecotizar={recotizarExpirada}
          sel={sel}
          setSel={setSel}
          onPay={(t) => openDil(t.id)}
          goHist={() => switchTab('historial')}
        />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                {/* Próx: columna de selección múltiple. */}
                {exp && <th style={{ width: 32 }} />}
                <SortHead
                  label="Diligencia"
                  k="dil"
                  sortKey={sort.key}
                  sortBy={sort.dir}
                  onSort={onSort}
                />
                <SortHead
                  label="Estado"
                  k="estado"
                  sortKey={sort.key}
                  sortBy={sort.dir}
                  onSort={onSort}
                  w="22%"
                />
                <Th w="15%">Encargado</Th>
                <SortHead
                  label="Solicitada para"
                  k="deadline"
                  sortKey={sort.key}
                  sortBy={sort.dir}
                  onSort={onSort}
                  w="15%"
                  num
                  tip="Plazo solicitado por el estudio"
                />
                <Th w="16%" num>
                  Acción
                </Th>
              </tr>
            </thead>
            <tbody>
              {activeTableRows.map((t) => {
                const st = taskStage(t);
                return (
                  <tr
                    key={t.id}
                    data-urgent={st.urgent ? 'true' : undefined}
                    onClick={() => openDil(t.id)}
                  >
                    {exp && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <label className="t-check">
                          <input
                            type="checkbox"
                            checked={!!sel[t.id]}
                            onChange={() =>
                              setSel((s) => ({ ...s, [t.id]: !s[t.id] }))
                            }
                          />
                        </label>
                      </td>
                    )}
                    <td>
                      <div className="tbl__id">{dilId(t)}</div>
                      <div
                        className="tbl__primary"
                        style={{ fontWeight: 400, marginTop: 3 }}
                      >
                        {caratulaOf(t)}
                      </div>
                      <div className="tbl__secondary">
                        {tableSecondaryOf(t)}
                      </div>
                    </td>
                    <td>
                      <div>
                        <StagePill tone={st.tone} label={st.label} />
                        {st.statusText && (
                          <div
                            className="tbl__secondary"
                            style={{ marginTop: 4 }}
                          >
                            {st.statusText}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="note">{t.assigneeName || '—'}</td>
                    <td className="num">
                      <DeadlineCell t={t} />
                    </td>
                    <td className="num" onClick={(e) => e.stopPropagation()}>
                      <ActionBtn t={t} onPay={() => openDil(t.id)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pager
            page={activePage}
            pageCount={activePageCount}
            total={activeTableSorted.length}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={(n) => {
              setPageSize(n);
              setPage(1);
            }}
            sizes={[8, 16, 24, 48]}
            unit="diligencias"
          />
        </div>
      )}
    </div>
  );
}
